import { useCallback, useMemo, useState } from 'react';
import useGetAttachmentUploadUrl from './useAttachmentUploadUrlMutation';
import useAttachmentCreate from './useAttachmentCreateMutation';
import useAttachmentDeleteMutation from './useAttachmentDeleteMutation';
import useGetAttachmentsList from './useAttachmentListQuery';
import getAttachmentQuery from './useAttachmentQuery';

export type UseAttachmentManagerOptions = {
  relatedTable: string; // e.g. 'models'
  relatedItem: string; // record id
  attachmentType?: 'general' | 'profile';
  relatedField?: string; // e.g. picture
};

// Minimal shape we rely on from the attachments list
export interface AttachmentListItem {
  id: string;
  mimeType?: string | null;
  path?: string | null;
  originalFileName?: string | null;
  downloadUrl?: string | null;
  size?: number | null;
  type?: string | null;
}

const buildConditionString = (relatedTable: string, relatedItem: string, type: string) => {
  const base = [['relatedItem', '=', relatedItem], 'and', ['relatedTable', '=', relatedTable]];
  if (type) {
    base.push('and', ['type', '=', type]);
  }
  return JSON.stringify(base);
};

const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const useAttachmentManager = ({
  relatedTable,
  relatedItem,
  attachmentType = 'general',
  relatedField,
}: UseAttachmentManagerOptions) => {
  // local state
  const [file, setFile] = useState<File | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [busyDeleteId, setBusyDeleteId] = useState<string | null>(null);

  // existing hooks
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();
  const { deleteAttachment } = useAttachmentDeleteMutation();
  const { getAttachment } = getAttachmentQuery();

  // list current attachments for this record
  const conditionString = useMemo(
    () => buildConditionString(relatedTable, relatedItem, attachmentType),
    [relatedTable, relatedItem, attachmentType],
  );
  const listQuery: any = useGetAttachmentsList({ conditionString });

  const currentAttachments: AttachmentListItem[] = useMemo(() => {
    const raw = listQuery?.data?.getAttachmentList?.data;
    return Array.isArray(raw) ? (raw as AttachmentListItem[]) : [];
  }, [listQuery?.data]);

  // helpers
  const refetch = useCallback(() => {
    if (typeof listQuery?.refetch === 'function') {
      void listQuery.refetch();
    }
  }, [listQuery]);

  const pickCurrentImageAttachment = useCallback((): AttachmentListItem | null => {
    const imgs = (currentAttachments || []).filter(
      (a) => typeof a?.mimeType === 'string' && a.mimeType!.includes('image'),
    );

    return imgs.length ? imgs[0] : null;
  }, [currentAttachments]);

  const clearPreview = useCallback(() => {
    if (previewSrc) URL.revokeObjectURL(previewSrc);
    setPreviewSrc(null);
    setFile(null);
  }, [previewSrc]);

  const onPickFile = useCallback(
    (f: File) => {
      if (!f) return;

      if (f.size > MAX_SIZE_BYTES) {
        throw new Error(
          `File is too large. Max size is ${Math.round(MAX_SIZE_BYTES / (1024 * 1024))} MB.`,
        );
      }

      const url = URL.createObjectURL(f);
      setPreviewSrc(url);
      setFile(f);
    },
    [MAX_SIZE_BYTES],
  );

  // Open attachment in a new tab (uses presigned download URL)
  const open = useCallback(
    async (attachmentId: string) => {
      const resp: any = await getAttachment(attachmentId);
      const downloadUrl = resp?.data?.getAttachment?.data?.downloadUrl as string | undefined;
      if (downloadUrl) window.open(downloadUrl, '_blank');
    },
    [getAttachment],
  );

  // Upload selected files
  const upload = useCallback(async () => {
    if (!file) return;

    const uploadInfos = await getAttachmentUploadUrls([
      {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
        relatedTable,
        relatedItem,
        type: attachmentType,
      },
    ]);

    const uploadInfo = Array.isArray(uploadInfos)
      ? uploadInfos.find((u: any) => u && u.uploadUrl && u.uploadName)
      : undefined;

    if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
      throw new Error('Failed to get upload URL');
    }

    await fetch(uploadInfo.uploadUrl as string, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    await createAttachment({
      fileName: String(uploadInfo.uploadName).split('/').pop() || '',
      size: file.size,
      mimeType: file.type,
      relatedTable,
      relatedItem,
      relatedField,
      type: attachmentType,
      path: uploadInfo.uploadName as string,
      originalFileName: uploadInfo.originalFileName as string,
    });

    refetch();
  }, [
    file,
    getAttachmentUploadUrls,
    createAttachment,
    relatedTable,
    relatedItem,
    clearPreview,
    refetch,
  ]);

  const reupload = useCallback(async () => {
    if (!file) return;

    // Delete all existing attachments of type "profile"
    const deletions = currentAttachments
      .filter((x) => x.type === 'profile')
      .map(async (attachment) => {
        try {
          await deleteAttachment(attachment.id, relatedField);
        } catch (err) {
          console.warn('Failed to delete attachment during reupload', attachment.id, err);
        }
      });
    await Promise.all(deletions);
    await upload();
  }, [file, currentAttachments, deleteAttachment, upload]);

  // Delete with confirm + refresh
  const removeCurrentImage = useCallback(async () => {
    const img = pickCurrentImageAttachment();

    if (!img) return;
    setBusyDeleteId(img.id);
    try {
      await deleteAttachment(img.id, img.type === 'profile' ? relatedField : undefined);
      clearPreview();
      refetch();
    } finally {
      setBusyDeleteId(null);
    }
  }, [pickCurrentImageAttachment, deleteAttachment, clearPreview, refetch]);

  const files: File[] = file ? [file] : [];
  const onSelectedFileChanged = useCallback(
    (vc: { value?: File[] } | any) => {
      const arr = vc?.value as File[] | undefined;
      if (arr?.[0]?.name !== file?.name) {
        if (arr && arr.length) {
          onPickFile(arr[0]);
        } else {
          clearPreview();
        }
      }
    },
    [file, onPickFile, clearPreview],
  );

  const remove = useCallback(
    async (arg?: string | { id: string; originalFileName?: string }) => {
      const id = typeof arg === 'string' ? arg : arg?.id;
      if (!id) {
        await removeCurrentImage();
        return;
      }
      setBusyDeleteId(id);
      try {
        await deleteAttachment(id);
        clearPreview();
        refetch();
      } finally {
        setBusyDeleteId(null);
      }
    },
    [removeCurrentImage, deleteAttachment, clearPreview, refetch],
  );

  const currentlyDeleting: string[] = busyDeleteId ? [busyDeleteId] : [];

  const currentlyUploading:
    | { originalFileName: string; id: string; mimeType: string; size: number }[]
    | [] = file
    ? [{ originalFileName: file.name, id: file.name, mimeType: 'uploading', size: file.size }]
    : [];

  return {
    file,
    previewSrc,
    busyDeleteId,
    currentAttachments,
    onPickFile,
    clearPreview,
    upload,
    reupload,
    removeCurrentImage,
    open,
    refetch,
    files,
    onSelectedFileChanged,
    remove,
    currentlyDeleting,
    currentlyUploading,
  };
};

export default useAttachmentManager;
